import { useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X } from "lucide-react";
import { useCreatePO } from "@/hooks/usePurchaseOrders";
import { useVendorContacts } from "@/hooks/useVendorContacts";
import { VendorSearchInput } from "@/components/shared/VendorSearchInput";

export function CreatePODialog() {
  const [open, setOpen] = useState(false);
  const [vendorId, setVendorId] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [notes, setNotes] = useState("");

  const createPO = useCreatePO();
  const { data: vendors = [] } = useVendorContacts();
  const selectedVendor = vendors.find((v) => v.id === vendorId);

  const resetForm = () => {
    setVendorId(""); setOrderDate(""); setExpectedDate(""); setNotes("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createPO.mutate(
      {
        vendor_id: vendorId || undefined,
        order_date: orderDate || undefined,
        expected_date: expectedDate || undefined,
        notes: notes || undefined,
      },
      { onSuccess: () => { resetForm(); setOpen(false); } }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="h-4 w-4 mr-2" />New PO</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Purchase Order</DialogTitle>
          <DialogDescription>Create a purchase order for parts or supplies.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Vendor</Label>
            {selectedVendor ? (
              <div className="flex items-center gap-2 h-9 px-3 rounded-md border bg-muted/40 text-sm">
                <span className="flex-1 truncate">{selectedVendor.company}</span>
                <button type="button" onClick={() => setVendorId("")} className="text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <VendorSearchInput vendors={vendors} onSelect={(v) => setVendorId(v.id)} />
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Order Date</Label>
              <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Expected Date</Label>
              <Input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={createPO.isPending}>
              {createPO.isPending ? "Creating..." : "Create PO"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
